<!--
  AI Analysis pane — extracted from message-viewer so the Ticket screen can show the same
  analysis without duplicating it. Scoped to ONE ChannelMessage: an Issue carries no confidence
  score of its own, so the host picks which message to analyse and passes it in.
-->
<ng-container *ngIf="message">
        <!-- Re-evaluating placeholder -->
        <div class="rescore-progress" *ngIf="isRescoring">
          <div class="rescore-spinner">
            <div class="izzy-mark-pulse" aria-hidden="true"></div>
          </div>
          <div class="rescore-status">Re-evaluating with current rubric...</div>
          <div class="rescore-hint">AI is scoring across 6 categories</div>
        </div>

        <!-- Rescore Button -->
        <div class="rescore-action-row" *ngIf="!isRescoring && canRescore">
          <button class="rescore-btn"
                  [disabled]="isReprocessing"
                  (click)="rescoreRequested.emit()"
                  title="Re-run confidence evaluator with current rubric">
            <i [class]="isReprocessing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-scale-balanced'"></i>
            {{ isReprocessing ? 'Evaluating...' : 'Re-evaluate Score' }}
          </button>
        </div>

        <!-- Confidence Score -->
        <div class="analysis-section" *ngIf="!isRescoring && message.AIConfidenceScore !== null && message.AIConfidenceScore !== undefined">
          <div class="confidence-header-row">
            <div class="confidence-score-display" [ngClass]="getConfidenceScoreClass(message.AIConfidenceScore)">
              <span class="score-number">{{ message.AIConfidenceScore }}</span>
              <span class="score-max">/ 100</span>
            </div>
            <span class="auto-approved-badge" *ngIf="message.AIAutoApproved">
              <i class="fa-solid fa-bolt"></i> Auto-Approved
            </span>
            <span class="manual-review-badge" *ngIf="!message.AIAutoApproved && isPending">
              <i class="fa-solid fa-user-check"></i> Needs Review
            </span>
          </div>
          <div class="confidence-meter">
            <div class="meter-track">
              <div class="meter-fill" [style.width.%]="message.AIConfidenceScore" [ngClass]="getConfidenceScoreClass(message.AIConfidenceScore)"></div>
              <div class="meter-threshold" [style.left.%]="getAutoApproveThreshold()"></div>
            </div>
            <div class="meter-labels">
              <span>0</span>
              <span class="threshold-label" [style.left.%]="getAutoApproveThreshold()">{{ getAutoApproveThreshold() }}%</span>
              <span>100</span>
            </div>
          </div>
          <div class="confidence-guide">
            <div class="confidence-guide-row guide-high">
              <span class="guide-range">85 – 100</span>
              <span class="guide-desc">High confidence — reply is likely accurate and complete</span>
            </div>
            <div class="confidence-guide-row guide-medium">
              <span class="guide-range">60 – 84</span>
              <span class="guide-desc">Medium confidence — human review recommended</span>
            </div>
            <div class="confidence-guide-row guide-low">
              <span class="guide-range">0 – 59</span>
              <span class="guide-desc">Low confidence — close review required before approving</span>
            </div>
            <div class="confidence-threshold-note">
              <i class="fa-solid fa-bolt"></i>
              Replies at or above <strong>{{ getAutoApproveThreshold() }}%</strong> are auto-approved for this channel
            </div>
          </div>
        </div>

        <!-- AI Reasoning - Structured Category Breakdown -->
        <div class="analysis-section" *ngIf="!isRescoring && message.AIConfidenceReason">
          <div class="analysis-section-title">
            <i class="fa-solid fa-lightbulb"></i>
            AI Reasoning
          </div>

          <!-- Structured category cards (new rubric format) -->
          <ng-container *ngIf="parsedCategoryBreakdown as categories; else rawReasoning">
            <div class="category-breakdown">
              <div class="category-card"
                   *ngFor="let cat of categories; let i = index"
                   [class.animate-in]="animateCategories"
                   [style.animation-delay]="animateCategories ? (i * 150) + 'ms' : '0ms'">
                <div class="category-card-header">
                  <div class="category-name">
                    <i class="fa-solid" [ngClass]="cat.icon"></i>
                    {{ cat.name }}
                  </div>
                  <div class="category-score-badge" [ngClass]="getCategoryScoreClass(cat.percentage)">
                    {{ cat.score }}<span class="score-max-small"> / {{ cat.maxScore }}</span>
                  </div>
                </div>
                <div class="category-mini-bar">
                  <div class="category-mini-fill"
                       [style.width.%]="cat.percentage"
                       [ngClass]="getCategoryScoreClass(cat.percentage)">
                  </div>
                </div>
                <div class="category-explanation">{{ cat.explanation }}</div>
              </div>
            </div>

            <!-- Force review / disqualifier note -->
            <div class="reasoning-note warning" *ngIf="categoryBreakdownNote">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <span>{{ categoryBreakdownNote }}</span>
            </div>

            <!-- Safety notice (forced generation) -->
            <div class="reasoning-note danger" *ngIf="safetyNoticeText">
              <i class="fa-solid fa-shield-exclamation"></i>
              <span><strong>Safety Notice:</strong> {{ safetyNoticeText }}</span>
            </div>
          </ng-container>

          <!-- Fallback: raw markdown for older messages without structured format -->
          <ng-template #rawReasoning>
            <div class="analysis-content markdown-content" [innerHTML]="renderMarkdown(message.AIConfidenceReason)"></div>
          </ng-template>
        </div>

        <!-- Skip Reasoning (if skipped or manually marked not required) -->
        <div class="analysis-section" *ngIf="(message.GenerationStatus === 'Skipped' || message.ApprovalStatus === 'Not Required') && message.SkipReasoning">
          <div class="analysis-section-title skip">
            <i class="fa-solid fa-forward"></i>
            Skip Reasoning
          </div>
          <div class="analysis-content markdown-content" [innerHTML]="renderMarkdown(message.SkipReasoning)"></div>
        </div>

        <!-- Error Info (if failed) -->
        <ng-container *ngIf="getFriendlyError() as friendlyError">
          <div class="analysis-section error-section">
            <div class="analysis-section-title error">
              <i class="fa-solid fa-circle-exclamation"></i>
              Error Details
            </div>
            <div class="error-details">
              <div class="error-type-row">
                <span class="error-label">Type:</span>
                <span class="error-type" [ngClass]="friendlyError.cssClass">{{ friendlyError.title }}</span>
              </div>
              <div class="error-friendly-summary">{{ friendlyError.summary }}</div>
              <div class="error-friendly-action" *ngIf="friendlyError.action">{{ friendlyError.action }}</div>
              <button type="button" class="error-cta-btn inline" (click)="openAICredentials()" *ngIf="friendlyError.isAICredentialIssue">
                <i class="fa-solid fa-key"></i> Check AI credentials
              </button>
              <div class="error-raw-label" *ngIf="message.ErrorMessage">Raw error</div>
              <div class="error-message-box" *ngIf="message.ErrorMessage">{{ message.ErrorMessage }}</div>
            </div>
          </div>
        </ng-container>

        <!-- Reply Diff (if edited) -->
        <div class="analysis-section" *ngIf="message.ApprovedReplyContent && message.ApprovedReplyContent !== message.GeneratedReplyContent">
          <div class="analysis-section-title">
            <i class="fa-solid fa-code-compare"></i>
            Changes Made
          </div>
          <div class="diff-view-container">
            <izzy-diff-viewer
              [originalContent]="message.GeneratedReplyContent || ''"
              [modifiedContent]="message.ApprovedReplyContent || ''"
              [showLegend]="true"
              title="Edits">
            </izzy-diff-viewer>
          </div>
        </div>

        <!-- No Analysis Available -->
        <div class="empty-tab-state" *ngIf="!isRescoring && message.AIConfidenceScore === null && !message.AIConfidenceReason && !message.SkipReasoning && !message.ErrorType">
          <i class="fa-solid fa-chart-simple"></i>
          <span>No AI analysis available for this message</span>
        </div>
</ng-container>
